import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { FileSpreadsheet, FileText, Users, Package, Hash } from 'lucide-react';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PartywiseReportDialog = ({ open, onOpenChange, eventId, eventName }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [sortBy, setSortBy] = useState('default');

  useEffect(() => {
    if (!open || !eventId) { setReport(null); return; }
    (async () => {
      setLoading(true);
      setReport(null);
      try {
        const res = await api.get(`/events/${eventId}/partywise-report`);
        setReport(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load partywise report');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, eventId]);

  const handleExport = async (fmt) => {
    if (!eventId) return;
    setExporting(fmt);
    try {
      const res = await api.get(
        `/events/${eventId}/partywise-report/export?fmt=${fmt}`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (eventName || 'event').replace(/\s+/g, '_');
      a.download = `partywise-report-${safeName}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      let detail = 'Failed to download';
      if (err.response?.data?.text) {
        try {
          const t = await err.response.data.text();
          detail = JSON.parse(t).detail || detail;
        } catch { /* noop */ }
      }
      toast.error(detail);
    } finally {
      setExporting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="partywise-report-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-indigo-600" />
            Partywise Qty Report
            {eventName && <span className="text-indigo-600 font-normal text-base">— {eventName}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Order-wise party quantity breakdown grouped by date.
          </DialogDescription>
        </DialogHeader>

        {/* Export buttons */}
        {report && !loading && (
          <div className="flex items-center gap-2 flex-shrink-0 pb-2 border-b flex-wrap" data-testid="partywise-export-bar">
            <div className="flex-1 text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{report.total_orders}</span> orders
              <span className="mx-1.5 text-gray-300">|</span>
              <span className="font-semibold text-gray-900">{report.grand_total?.toLocaleString('en-IN')}</span> pcs total
            </div>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="h-8 px-2 text-xs border border-gray-300 rounded-md focus:outline-none" data-testid="partywise-sort">
              <option value="default">Sort: Date → Name</option>
              <option value="name_asc">Party Name A→Z</option>
              <option value="name_desc">Party Name Z→A</option>
              <option value="qty_desc">Qty High→Low</option>
              <option value="qty_asc">Qty Low→High</option>
              <option value="state_asc">State A→Z</option>
              <option value="agent_asc">Agent A→Z</option>
              <option value="distributor_asc">Distributor A→Z</option>
            </select>
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              disabled={exporting === 'xlsx'}
              onClick={() => handleExport('xlsx')}
              data-testid="partywise-export-xlsx"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />
              {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              disabled={exporting === 'pdf'}
              onClick={() => handleExport('pdf')}
              data-testid="partywise-export-pdf"
            >
              <FileText className="w-4 h-4 mr-1.5" />
              {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
            </Button>
          </div>
        )}

        {/* Report body */}
        <div className="flex-1 overflow-y-auto min-h-0 pr-1" data-testid="partywise-report-body">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          )}

          {!loading && report && report.date_groups?.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No orders found for this event.</p>
            </div>
          )}

          {!loading && report && report.date_groups?.length > 0 && (
            <div className="space-y-6">
              {report.date_groups.map((grp) => (
                <div key={grp.date} data-testid={`partywise-date-group-${grp.date}`}>
                  {/* Date header */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-t-lg px-4 py-2 flex items-center justify-between">
                    <h3 className="text-sm font-bold text-indigo-800">
                      {grp.date} {report.event_name}
                    </h3>
                    <span className="text-xs text-indigo-600 font-medium">
                      {grp.order_count} order{grp.order_count !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Table */}
                  <div className="border border-t-0 border-gray-200 rounded-b-lg overflow-hidden">
                    <table className="w-full text-sm" data-testid={`partywise-table-${grp.date}`}>
                      <thead>
                        <tr className="bg-gray-900 text-white text-xs">
                          <th className="px-3 py-2 text-center w-12">SR NO.</th>
                          <th className="px-3 py-2 text-center w-32">ORDER FORM</th>
                          <th className="px-3 py-2 text-left">PARTY NAME</th>
                          <th className="px-3 py-2 text-left w-28">AREA</th>
                          <th className="px-3 py-2 text-left w-28">STATE</th>
                          <th className="px-3 py-2 text-left w-28">AGENT</th>
                          <th className="px-3 py-2 text-left w-28">DISTRIBUTOR</th>
                          <th className="px-3 py-2 text-center w-16">QTY</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let sorted = [...grp.rows];
                          if (sortBy !== 'default') {
                            sorted.sort((a, b) => {
                              switch (sortBy) {
                                case 'name_asc': return (a.party_name || '').localeCompare(b.party_name || '');
                                case 'name_desc': return (b.party_name || '').localeCompare(a.party_name || '');
                                case 'qty_desc': return (b.qty || 0) - (a.qty || 0);
                                case 'qty_asc': return (a.qty || 0) - (b.qty || 0);
                                case 'state_asc': return (a.state || '').localeCompare(b.state || '');
                                case 'agent_asc': return (a.agent_name || '').localeCompare(b.agent_name || '');
                                case 'distributor_asc': return (a.distributor_name || '').localeCompare(b.distributor_name || '');
                                default: return 0;
                              }
                            });
                          }
                          return sorted.map((r, idx) => (
                          <tr
                            key={`${grp.date}-${r.so_number}`}
                            className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-3 py-1.5 text-center text-gray-500 text-xs">{idx + 1}</td>
                            <td className="px-3 py-1.5 text-center font-mono text-xs text-gray-700">{r.so_number}</td>
                            <td className="px-3 py-1.5 font-medium text-gray-900">{r.party_name}</td>
                            <td className="px-3 py-1.5 text-gray-600 text-xs">{r.area}</td>
                            <td className="px-3 py-1.5 text-gray-600 text-xs">{r.state || '—'}</td>
                            <td className="px-3 py-1.5 text-gray-600 text-xs">{r.agent_name || '—'}</td>
                            <td className="px-3 py-1.5 text-gray-600 text-xs">{r.distributor_name || '—'}</td>
                            <td className="px-3 py-1.5 text-center font-semibold text-gray-900">{r.qty?.toLocaleString('en-IN')}</td>
                          </tr>
                          ));
                        })()}
                        {/* Day total */}
                        <tr className="bg-emerald-50 border-t-2 border-emerald-200">
                          <td colSpan={7} className="px-3 py-2 text-right font-bold text-emerald-800 text-sm">
                            TOTAL
                          </td>
                          <td className="px-3 py-2 text-center font-bold text-emerald-800 text-sm">
                            {grp.total?.toLocaleString('en-IN')}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {/* Grand total */}
              <div
                className="bg-amber-50 border-2 border-amber-300 rounded-lg px-4 py-3 flex items-center justify-between"
                data-testid="partywise-grand-total"
              >
                <span className="text-sm font-bold text-amber-800 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  GRAND TOTAL
                </span>
                <span className="text-lg font-bold text-amber-900">
                  {report.grand_total?.toLocaleString('en-IN')} pcs
                </span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PartywiseReportDialog;
